import { memo, useCallback, useMemo } from 'react'

import { Button, Text } from '@cash-me/react-components'
import Image from 'next/image'

import { Carousel } from 'components/carousel'
import { Col, Grid } from 'components/grid'
import useWindowSize from 'hooks/useWindowSize'
import { pushDataLayer } from 'utils/push-data-layer'

import { condominiumCreditSteps } from './data'
import * as S from './styles'

const SLIDE_WIDTH_BREAKPOINT = 380
const MAX_SLIDES_DESKTOP = 3.2
const SLIDES_TABLET = 2.5
const SLIDES_MOBILE = 1.2

type Props = {
  onCtaClick: () => void
}

export const CondominiumCreditHowItWorks = memo(({ onCtaClick }: Props) => {
  const { width, isTablet, isDesktop } = useWindowSize()

  const slidesPerView = useMemo(() => {
    if (isDesktop) return MAX_SLIDES_DESKTOP
    if (isTablet) return SLIDES_TABLET
    if (width)
      return width > SLIDE_WIDTH_BREAKPOINT ? Math.max(width / SLIDE_WIDTH_BREAKPOINT, SLIDES_MOBILE) : SLIDES_MOBILE
    return SLIDES_MOBILE
  }, [width, isTablet, isDesktop])

  const handleDataLayer = useCallback((index: number) => {
    pushDataLayer({
      category: 'Slider',
      action: 'Crédito para Condomínios',
      label: `Como funciona - Slide ${condominiumCreditSteps[index].title}`
    })
  }, [])

  return (
    <S.Section $bg="contrast" aria-labelledby="condominium-credit-how-it-works-title">
      <Grid col={{ phone: 1, tablet: 1 }}>
        <Col col={{ desktop: 10 }} start={{ desktop: 2 }}>
          <S.TitleWrapper>
            <Text
              id="condominium-credit-how-it-works-title"
              as="h2"
              size="heading2"
              weight="medium"
              color="var(--colors-neutral-text-contrast-default)"
            >
              Como funciona o{' '}
              <Text as="span" size="heading2" weight="medium" color="var(--colors-pink-text-contrast)">
                Crédito para Condomínios
              </Text>{' '}
              da CashMe?
            </Text>
            <Text color="var(--colors-neutral-text-contrast-default)">
              O processo foi pensado para ajudar o condomínio a entender as possibilidades antes de tomar uma decisão.
            </Text>
          </S.TitleWrapper>
        </Col>

        <Col col={{ desktop: 10 }} start={{ desktop: 2 }}>
          <S.CarouselWrapper>
            <Carousel onChangeIndex={handleDataLayer} navigation={false} swiperOptions={{ slidesPerView }}>
              {condominiumCreditSteps.map(({ id, image, imageAlt, icon, content }) => (
                <S.Slide key={id}>
                  <S.BackgroundLine />
                  <S.IconWrapper>{icon}</S.IconWrapper>
                  <S.CardWrapper>
                    <S.ImageContainer>
                      <Image
                        src={image}
                        alt={imageAlt}
                        fill
                        style={{ objectFit: 'cover', objectPosition: 'top' }}
                        sizes="(max-width: 768px) 100vw, 380px"
                        loading="lazy"
                      />
                    </S.ImageContainer>
                    <S.CardContent>{content(isTablet)}</S.CardContent>
                  </S.CardWrapper>
                </S.Slide>
              ))}
            </Carousel>
          </S.CarouselWrapper>

          <S.CtaWrapper>
            <Button
              aria-label="Solicitar uma simulação de crédito para condomínio"
              size="lg"
              type="button"
              variants="text-only"
              onClick={onCtaClick}
            >
              <span>Solicitar uma simulação</span>
            </Button>
          </S.CtaWrapper>
        </Col>
      </Grid>
    </S.Section>
  )
})

CondominiumCreditHowItWorks.displayName = 'CondominiumCreditHowItWorks'
